import { Sidebar } from '../components/Sidebar';
import { EventCard } from '../components/EventCard';
import { EventPreview } from '../components/EventPreview';
import { Calendar, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { isEventActive } from '../lib/helpers';
import { useState, useEffect } from 'react';
import {
  OrganizerPendingView,
  OrganizerRejectedView,
  OrganizerViewerView,
  OrganizerErrorView,
} from '../components/OrganizerStatusViews';

export const OrganizerPastEvents = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewEvent, setPreviewEvent] = useState(null);

  // Load user's events
  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadEvents = async () => {
  if (!user) return;
  setLoading(true);

  const data = await eventService.getEventsByOrganizer(user.uid);

  setEvents(
    data.map(e => ({
      ...e,
      time: e.time ?? '',
      startDate: e.startDate || e.date,
      endDate: e.endDate || e.date,
    }))
  );

  setLoading(false);
};

  // Filter past events only (mesma base temporal usada em "Ativos": getEventDateTime)
  const getPastEvents = () => {
    return events
      .filter(event => !isEventActive(event))
      .sort((a, b) => {
        // Sort by date descending (most recent first)
        const aDate = a.startDate?.toDate?.() || new Date(a.startDate);
const bDate = b.startDate?.toDate?.() || new Date(b.startDate);

aDate.setHours(0, 0, 0, 0);
bDate.setHours(0, 0, 0, 0);

return bDate - aDate;
      });
  };

  const organizerStatus = user?.organizerStatus;

  if (organizerStatus && organizerStatus !== 'approved') {
    let statusView;
    if (organizerStatus === 'pending') {
      statusView = <OrganizerPendingView />;
    } else if (organizerStatus === 'rejected') {
      statusView = <OrganizerRejectedView />;
    } else if (organizerStatus === 'none' || organizerStatus === 'viewer') {
      statusView = <OrganizerViewerView />;
    } else {
      statusView = <OrganizerErrorView />;
    }
    return (
      <div className="min-h-screen bg-background flex">
        <Sidebar />
        <main className="flex-1 p-4 pt-16 md:p-8 overflow-auto flex items-center justify-center">
          {statusView}
        </main>
      </div>
    );
  }

  const pastEvents = getPastEvents();

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />

      <main className="flex-1 p-4 pt-16 md:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Clock className="w-10 h-10 text-muted-foreground" />
              Eventos Encerrados
            </h1>
            <p className="text-muted-foreground mt-2">
              Histórico de eventos que já aconteceram
            </p>
          </div>

          {/* Stats */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="bg-muted p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-3xl font-bold">{pastEvents.length}</p>
                <p className="text-sm text-muted-foreground">
                  {pastEvents.length === 1 ? 'evento encerrado' : 'eventos encerrados'}
                </p>
              </div>
            </div>
          </div>

          {/* Events Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Carregando eventos...</p>
            </div>
          ) : pastEvents.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">Nenhum evento encerrado</h3>
              <p className="text-muted-foreground">
                Seus eventos passados aparecerão aqui após a data de realização.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-6">
              {pastEvents.map((event) => (
                <div key={event.id} className="relative opacity-80 hover:opacity-100 transition-opacity">
                  {/* "Encerrado" badge */}
                  <div className="absolute top-3 left-3 z-10 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-semibold">
                    Encerrado
                  </div>
                  <EventCard
                    event={event}
                    onPreview={() => setPreviewEvent(event)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Event Preview Modal */}
      {previewEvent && (
        <EventPreview
          event={previewEvent}
          onClose={() => setPreviewEvent(null)}
        />
      )}
    </div>
  );
};

